/* eslint-disable jsx-a11y/click-events-have-key-events */
import axios from 'axios';
import cl from 'classnames';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Demo from './SkeletonDemo';
import Select from '../Select/Select';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import LocalitiesAutocomplete from '../Woosmap/LocalitiesAutocomplete';
import CountrySelect, { countriesOptions } from '../Select/CountrySelect';
import Constants from '../../Constants';
import { tr } from '../utils/locale';
import markerIcon from '../../images/marker.png';
import { WoosmapMapBoundingBox, createWoosmapMap } from '../Map/drawOnMap';

const languages = [
    { value: 'fr', label: 'French' },
    { value: 'gb', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'nl', label: 'Dutch' },
];

export default class LocalitiesDemo extends Component {
    constructor(props) {
        super(props);
        const types = [
            { label: tr('Localities'), value: 'locality' },
            { label: tr('Postal codes'), value: 'postal_code' },
            { label: tr('Address'), value: 'address' },
            {
                label: tr('Administrative areas (States, regions, ...)'),
                value: 'admin_level',
            },
            { label: tr('Airports'), value: 'airport' },
            { label: tr('Train stations'), value: 'train_station' },
            { label: tr('Metro stations'), value: 'metro_station' },
            { label: tr('Shopping centers'), value: 'shopping' },
            { label: tr('Museums'), value: 'museum' },
            { label: tr('Tourist attractions'), value: 'tourist_attraction' },
            { label: tr('Amusement parks'), value: 'amusement_park' },
            { label: tr('Art galleries'), value: 'art_gallery' },
            { label: tr('Zoos'), value: 'zoo' },
        ];
        const lang = (typeof window === 'object' && (navigator.language || navigator.userLanguage)) || 'gb';
        const defaultLang = languages.find((item) => lang.startsWith(item.value));
        this.state = {
            localities: null,
            selectedLocality: null,
            error: null,
            localitiesTypes: [],
            countries: [],
            showDetails: false,
            language: (defaultLang || languages[1]).value,
        };
        this.types = types.map((item) => ({
            label: item.label,
            value: item.value,
        }));
        this.mounted = true;
        this.map = null;
        this.marker = null;
        this.viewport = null;
        this.requestUrl = 'https://api.woosmap.com/localities/autocomplete/';
        this.requestUrlDetail = 'https://api.woosmap.com/localities/details/';
        this.demoRef = React.createRef();
        this.inputRef = React.createRef();
        this.overlay = null;
    }

    componentDidMount() {
        this.displayMap();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    typesContainsAddress = (localitiesTypes) => localitiesTypes.find((item) => item.value === 'address');

    getRequestparams = () => {
        const { selectedLocality, language } = this.state;
        return { public_id: selectedLocality?.public_id, key: Constants.woosmapKey, language };
    };

    selectLocality = (locality) => {
        this.setState({ selectedLocality: locality }, () => {
            if (locality) {
                axios
                    .get(this.requestUrlDetail, {
                        params: this.getRequestparams(),
                    })
                    .then((response) => {
                        if (this.mounted) {
                            this.setState(
                                {
                                    selectedLocality: response.data.result,
                                    showDetails: true,
                                    localities: response.data,
                                },
                                this.displayOnMap
                            );
                        }
                    })
                    .catch((error) => {
                        const errorResult = (error && error.response && error.response.data) || 'Unhandled error';
                        this.setState({ error: errorResult });
                    });
            }
        });
    };

    displayOnMap = () => {
        const { selectedLocality } = this.state;

        if (this.map && selectedLocality?.geometry?.location) {
            if (this.marker) {
                this.marker.setMap(null);
                this.marker = null;
            }

            if (this.overlay) {
                this.overlay.setMap(null);
                this.overlay = null;
            }

            this.marker = new window.woosmap.map.Marker({
                icon: { url: markerIcon, scaledSize: { height: 46, width: 30 } },
                position: selectedLocality.geometry.location,
                map: this.map,
            });

            if (selectedLocality.geometry.viewport) {
                const vp = selectedLocality.geometry.viewport;
                const bounds = {
                    north: vp.northeast.lat,
                    south: vp.southwest.lat,
                    east: vp.northeast.lng,
                    west: vp.southwest.lng,
                };
                this.overlay = new WoosmapMapBoundingBox(bounds);
                this.overlay.setMap(this.map);

                this.map.fitBounds(bounds, { top: 70, bottom: 70, left: 70, right: 70 });
            } else {
                this.map.setCenter(selectedLocality.geometry.location);
                if (selectedLocality.type === 'address') {
                    this.map.setZoom(10);
                }
            }
        }
    };

    // eslint-disable-next-line class-methods-use-this
    getHighlightedLocalityDesc = (locality) => {
        let { description } = locality;

        if (locality.matched_substrings && locality.matched_substrings.description) {
            for (let j = locality.matched_substrings.description.length - 1; j >= 0; j--) {
                const match = locality.matched_substrings.description[j];
                description = `${description.substring(
                    0,
                    match.offset
                )}<span class="localities-matched">${description.substring(
                    match.offset,
                    match.offset + match.length
                )}</span>${description.substring(match.offset + match.length)}`;
            }
        }
        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: description }} />;
    };

    renderResult = () => {
        const { selectedLocality } = this.state;
        if (!selectedLocality) {
            return <div className="demo__empty">{tr('No locality found')}</div>;
        }

        return (
            <>
                {selectedLocality.public_id && (
                    <p className="demo--localities__result-line">
                        <span className="demo--localities__result-line__type">Public id:</span>
                        <span className="demo--localities__result-line__info">{selectedLocality.public_id}</span>
                    </p>
                )}
                {selectedLocality.formatted_address && (
                    <p className="demo--localities__result-line">
                        <span className="demo--localities__result-line__type">Description:</span>
                        <span className="demo--localities__result-line__info">
                            {selectedLocality.formatted_address}
                        </span>
                    </p>
                )}
                {selectedLocality.types && selectedLocality.types[0] && (
                    <p className="demo--localities__result-line">
                        <span className="demo--localities__result-line__type">Type:</span>
                        <span className="demo--localities__result-line__info">
                            {selectedLocality.types[0].replace('_', ' ')}
                        </span>
                    </p>
                )}
                {selectedLocality.geometry?.accuracy && (
                    <p className="demo--localities__result-line">
                        <span className="demo--localities__result-line__type">Location type:</span>
                        <span className="demo--localities__result-line__info">
                            {selectedLocality.geometry?.accuracy.replace('_', ' ').toLowerCase()}
                        </span>
                    </p>
                )}
                {selectedLocality.geometry?.location && (
                    <p className="demo--localities__result-line two-lines">
                        <div className="two-lines--line">
                            <span className="demo--localities__result-line__type">Latitude:</span>
                            <span className="demo--localities__result-line__info">
                                {selectedLocality.geometry.location.lat.toString()}
                            </span>
                        </div>
                        <div className="two-lines--line">
                            <span className="demo--localities__result-line__type">Longitude:</span>
                            <span className="demo--localities__result-line__info">
                                {selectedLocality.geometry.location.lng.toString()}
                            </span>
                        </div>
                    </p>
                )}
                {selectedLocality.address_components && (
                    <div className="demo--localities__result-section">
                        <div className="demo--localities__result">Address components</div>
                        {selectedLocality.address_components.map((comp) => (
                            <div className="demo--localities__result-line">
                                <span className="demo--localities__result-line__type">{comp.types[0]}:</span>
                                <span className="demo--localities__result-line__info">
                                    {Array.isArray(comp.long_name) ? comp.long_name.join(', ') : comp.long_name}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    displayMap = () => {
        if (this.timeoutMap) {
            clearTimeout(this.timeoutMap);
        }

        if (window.woosmap && window.woosmap.map && this.demoRef.current) {
            window.woosmap.map.config.setApiKey(Constants.woosmapKey);
            this.map = createWoosmapMap(this.demoRef.current.getMap());
        } else {
            this.timeoutMap = setTimeout(this.displayMap, 300);
        }
    };

    renderHeaderFilters = () => {
        const { usecaseFilter } = this.props;
        const { language, localitiesTypes, countries } = this.state;
        const inputFilter = {
            label: tr('Type in '),
            component: (
                <LocalitiesAutocomplete
                    ref={this.inputRef}
                    woosmapKey={Constants.woosmapKey}
                    inputChangeCb={() => this.setState({ showDetails: false })}
                    placeholder={tr('Type in a locality name...')}
                    defaultLocality={this.defaultOrigin}
                    language={language}
                    responseCb={(localities) => this.setState({ localities })}
                    components={countries.map((item) => `country:${item.value}`).join('|')}
                    types={localitiesTypes.map((item) => item.value).join('|')}
                    callback={this.selectLocality}
                />
            ),
        };

        return [usecaseFilter, inputFilter];
    };

    renderFooterFilters = () => {
        const { language, countries } = this.state;
        const defaultCountry = countriesOptions.filter(
            (item) =>
                item.value === 'fr' ||
                item.value === 'gb' ||
                item.value === 'de' ||
                item.value === 'it' ||
                item.value === 'nl'
        );
        const filterTypes = {
            label: tr('Filter by'),
            component: (
                <>
                    <Select
                        placeholder={tr('Type of location')}
                        options={this.types}
                        isMulti
                        onChange={(items) =>
                            this.setState({
                                localitiesTypes: items,
                                countries: this.typesContainsAddress(items) ? defaultCountry : countries,
                            })
                        }
                    />
                    <CountrySelect
                        isMulti
                        placeholder={tr('Country')}
                        filter={['fr', 'gb', 'sp', 'it', 'de', 'nl']}
                        onChange={(items) => this.setState({ countries: items })}
                        value={countries}
                    />
                </>
            ),
        };
        const filterCountries = {
            label: tr('Language'),
            component: (
                <ButtonGroup className="language" isLight>
                    {languages.map((item) => (
                        <Button
                            size="small"
                            type="group"
                            key={item.value}
                            active={language === item.value}
                            onClick={() => {
                                this.setState({ language: item.value });
                            }}
                        >
                            <span className={cl('flag', `flag-${item.value}`)} title={item.label} />
                        </Button>
                    ))}
                </ButtonGroup>
            ),
        };
        return [filterTypes, filterCountries];
    };

    render() {
        const { localities, error, showDetails } = this.state;
        const { noheader, headerLabels, subHeader } = this.props;
        return (
            <Demo
                id="localities-demo"
                header={headerLabels}
                subHeader={subHeader}
                docLink="https://developers.woosmap.com/products/localities/get-started/"
                className="demo--localities"
                footerFilters={this.renderFooterFilters()}
                headerFilters={this.renderHeaderFilters()}
                request={showDetails ? this.requestUrlDetail : this.requestUrl}
                noheader={noheader}
                params={showDetails ? this.getRequestparams() : this.inputRef?.current?.getAutocompleteRequestparams()}
                referer="http://localhost/"
                response={error || localities}
                result={this.renderResult()}
                withMap
                ref={this.demoRef}
            />
        );
    }
}

LocalitiesDemo.defaultProps = {
    noheader: false,
    headerLabels: {},
    usecaseFilter: null,
    subHeader: null,
};
LocalitiesDemo.propTypes = {
    noheader: PropTypes.bool,
    headerLabels: PropTypes.object,
    usecaseFilter: PropTypes.object,
    subHeader: PropTypes.object,
};
